import Reflux from "reflux";
import Immutable, { fromJS } from "immutable";
import _, { set } from "lodash";
import { Big } from "big.js";

import AppConstants from "../constants/AppConstants";
import UIActions from "../actions/UIActions";
import {
  CROP_MODE,
  VERTICAL_RULER_WIDTH,
  LAYOUT_DESKTOP_WIDTH,
  LAYOUT_MOBILE_WIDTH,
  COLOR_MODE_AUTO,
  UI_SCALE_REGULAR,
  UI_SCALE_LARGE,
  UI_SCALE_SMALL,
  COLOR_MODE_LIGHT,
  COLOR_MODE_DARK,
  MAX_ZOOM,
  MIN_ZOOM,
  MOBILE_OVERLAY_OFFSET,
  ZOOM_TO_FIT_SCALE,
  DEFAULT_MODE,
  GROUP_FOCUS_MODE,
} from "../constants/UIConstants";
import { setBaseRem } from "../utils/UIUtils";
import { findXOutlierDistance } from "../utils/ZoomUtils";
import UndoRedoManager from "@/managers/UndoRedoManager";
import { isIOS } from "@/constants/BrowserConstants";

const SCALE_VALUES = [UI_SCALE_SMALL, UI_SCALE_REGULAR, UI_SCALE_LARGE];

const MODE_CONFIGS = fromJS({
  [CROP_MODE]: {
    type: CROP_MODE,
    undoRedo: false,
    allowEdits: false,
  },
  [DEFAULT_MODE]: {
    type: DEFAULT_MODE,
    undoRedo: true,
    allowEdits: true,
  },
  [GROUP_FOCUS_MODE]: {
    type: GROUP_FOCUS_MODE,
    undoRedo: true,
    allowEdits: true,
  },
});

const DesignViewStore = Reflux.createStore({
  storeName: "DesignViewStore",
  listenables: UIActions,

  init() {
    // set default color mode from system preference in state and apply to css
    this.systemColorMode = window.matchMedia("(prefers-color-scheme: light)")
      .matches
      ? COLOR_MODE_LIGHT
      : COLOR_MODE_DARK;
    this._applyColorModeClass(this.systemColorMode);
    // a portrait iPad has room beside the stage for one side panel
    this.portrait = isIOS ? window.matchMedia("(orientation: portrait)") : null;

    this.state = Immutable.fromJS({
      colorMode: this.systemColorMode,
      leftPanel: {
        // the properties panel is the one kept
        open: !this.portrait?.matches,
      },

      rightPanel: {
        open: true,
        activeProperty: null,
      },

      sitePanel: {
        activePanel: AppConstants.PANEL_PAGES,
        activePanelItemHistory: [],
      },

      stage: {
        // Split view is cramped on a tablet: touch starts desktop-only
        view: isIOS ? AppConstants.STAGE_VIEW_DESKTOP : AppConstants.STAGE_VIEW_SPLIT,
        focus: isIOS ? AppConstants.LAYOUT_DESKTOP : AppConstants.LAYOUT_MOBILE,
        viewHelpers: 0,
        overlays: {
          mobile: 0,
          guides: 1,
          columnGuides: 0,
          centerGuides: 0,
          enableSnapping: 1,
        },
        gridActive: false,
        guidesActive: false,
        mobile: {
          zoom: 1,
        },
        desktop: {
          zoom: 1,
        },
      },

      fontsPanel: {
        activeTab: AppConstants.FONT_MANAGER_TAB_GOOGLE,
      },
      themePanel: {
        activeTab: AppConstants.THEME_TAB_BRAND_COLORS,
      },

      propertiesPanel: {
        activePanel: AppConstants.PANEL_PROPERTIES_PAGE,

        pagePropertiesPanel: {
          activeSection: AppConstants.PANEL_SECTION_PAGE_BG,
        },
        blockPropertiesPanel: {
          activeSection: AppConstants.PANEL_SECTION_BLOCK_INFO,
        },
        statePropertiesPanel: {
          activeSection: AppConstants.PANEL_SECTION_STATE_INFO,
        },
        elementPropertiesPanel: {
          activeSection: AppConstants.PANEL_SECTION_ELEMENT_SIZE,
        },
        elementsPropertiesPanel: {
          activeSection: AppConstants.PANEL_SECTION_ELEMENTS_ALIGN,
        },
      },
      modals: {
        userBlogs: {
          state: AppConstants.MODAL_STATE_INACTIVE,
        },
        assetLibrary: {
          state: AppConstants.MODAL_STATE_INACTIVE,
        },
        userExports: {
          state: AppConstants.MODAL_STATE_INACTIVE,
        },
        designExport: {
          state: AppConstants.MODAL_STATE_INACTIVE,
        },
        designPreview: {
          state: AppConstants.MODAL_STATE_INACTIVE,
        },
        designPublish: {
          state: AppConstants.MODAL_STATE_INACTIVE,
        },
        blogSetup: {
          state: AppConstants.MODAL_STATE_INACTIVE,
        },
        domainManager: {
          state: AppConstants.MODAL_STATE_INACTIVE,
        },
        domainSetup: {
          state: AppConstants.MODAL_STATE_INACTIVE,
        },
        domainSettings: {
          state: AppConstants.MODAL_STATE_INACTIVE,
        },
        siteSettings: {
          state: AppConstants.MODAL_STATE_INACTIVE,
        },
        signup: {
          state: AppConstants.MODAL_STATE_INACTIVE,
        },
        designChooser: {
          state: AppConstants.MODAL_STATE_INACTIVE,
        },
        layoutChooser: {
          state: AppConstants.MODAL_STATE_INACTIVE,
        },
        rendererLockIn: {
          state: AppConstants.MODAL_STATE_INACTIVE,
        },
      },

      mode: MODE_CONFIGS.get(DEFAULT_MODE),

      activeTheme: null,

      activeAssetChooser: null,

      renamePopup: null,

      // controls which view is being shown for adding text, assets, blocks
      jsonEditorActive: false,
      working: false,
      workingMessage: "Working...",
      imageUploadOverlay: {
        show: false,
      },
    });

    this._loadUIPreferences();
    this.portrait?.addEventListener("change", (e) => this._onOrientationChange(e));
  },

  /** Turning an iPad to portrait with both side panels open closes the site panel, keeping the properties panel. */
  _onOrientationChange({ matches: portrait }) {
    const bothOpen =
      this.state.getIn(["leftPanel", "open"]) && this.state.getIn(["rightPanel", "open"]);
    if (!portrait || !bothOpen) return;
    this.state = this.state.setIn(["leftPanel", "open"], false);
    this.trigger(this.state);
  },

  /**
   * Opening a side panel on a portrait iPad closes the other.
   * @param {"leftPanel" | "rightPanel"} other
   */
  _closeOtherPanelInPortrait(other) {
    if (this.portrait?.matches) this.state = this.state.setIn([other, "open"], false);
  },

  onInitialize() {},

  onModalOpen(modalName, data) {
    const modal = this._setModalState(
      modalName,
      data,
      AppConstants.MODAL_STATE_OPEN,
    );
    if (modal) {
      if (!modal.getIn(["data", "overlay"], true)) {
        this._closeOtherModals(modalName);
      }
    }
  },

  onSelectModalTab(modalName, tabId) {
    console.log("onSelectModalTab", modalName, tabId);
    if (modalName && tabId) {
      this.state = this.state.setIn(
        ["modals", modalName, "selectedTab"],
        tabId,
      );
      this.trigger(this.state);
    }
  },
  onSelectModalChildTab(modalName, childTabId) {
    console.log("onSelectModalChildTab", modalName, childTabId);
    if (modalName && childTabId) {
      this.state = this.state.setIn(
        ["modals", modalName, "selectedChildTab"],
        childTabId,
      );
      this.trigger(this.state);
    }
  },
  onSetTabs(modalName, tabs) {
    console.log("onSetTabs", modalName, tabs);
    if (modalName && tabs) {
      this.state = this.state.setIn(["modals", modalName, "tabs"], tabs);
      this.trigger(this.state);
    }
  },
  onSetChildTabs(modalName, tabId, childTabs) {
    console.log("onSetChildTabs", modalName, tabId, childTabs);
    if (modalName && tabId && childTabs) {
      this.state = this.state.setIn(
        ["modals", modalName, "childTabs", tabId],
        childTabs,
      );
      this.trigger(this.state);
    } else {
      this.state = this.state.setIn(
        ["modals", modalName, "selectedChildTab"],
        null,
      );
      this.trigger(this.state);
    }
  },

  _applyColorModeClass(colorMode) {
    document.body.classList.remove(COLOR_MODE_LIGHT, COLOR_MODE_DARK);
    document.body.classList.add(
      `${colorMode === COLOR_MODE_AUTO ? this.systemColorMode : colorMode}`,
    );
  },

  onChangeColorMode(colorMode) {
    this.state = this.state.set("colorMode", colorMode);
    this._applyColorModeClass(colorMode);
    this._saveUIPreferences({ colorMode });
    this.trigger(this.state);
  },

  onToggleColorMode() {
    const colorMode =
      this.state.get("colorMode") === COLOR_MODE_LIGHT
        ? COLOR_MODE_DARK
        : COLOR_MODE_LIGHT;

    this.state = this.state.set("colorMode", colorMode);

    document.body.classList.remove(COLOR_MODE_LIGHT);
    document.body.classList.remove(COLOR_MODE_DARK);

    document.body.classList.add(
      `${colorMode === COLOR_MODE_AUTO ? this.systemColorMode : colorMode}`,
    );

    this._saveUIPreferences({ colorMode });
    this.trigger(this.state);
  },

  onToggleDensity() {
    const currentScale = this.state.get("uiScale");
    const currentIndex = SCALE_VALUES.indexOf(currentScale);
    const nextIndex = (currentIndex + 1) % SCALE_VALUES.length;

    this.onSetUIScale(SCALE_VALUES[nextIndex]);
  },

  onModalClose(modalName) {
    this._setModalState(modalName, false, AppConstants.MODAL_STATE_CLOSED);
  },

  onModalDeactivate(modalName) {
    this._setModalState(modalName, false, AppConstants.MODAL_STATE_INACTIVE);
  },

  _setModalState(modalName, data, state) {
    let modal = this.state.getIn(["modals", modalName], false);
    if (modal) {
      modal = modal.set("state", state);
      modal = modal.set("data", data);

      this.state = this.state.setIn(["modals", modalName], modal);

      this.trigger(this.state);
    }

    return modal;
  },

  isStageActive() {
    const modalOpened = this.state
      .get("modals")
      .some((modal) => modal.get("state") === AppConstants.MODAL_STATE_OPEN);
    return !modalOpened;
  },

  _closeOtherModals(activeModalName) {
    const modals = this.state.get("modals");
    modals.forEach(function (modal, modalName) {
      if (
        modalName != activeModalName &&
        modal.get("state") == AppConstants.MODAL_STATE_OPEN
      ) {
        const modalData = modal.toJS();
        const onHide = _.get(modalData, ["data", "onHide"], false);
        if (onHide) {
          onHide();
        } else {
          this._setModalState(
            modalName,
            false,
            AppConstants.MODAL_STATE_CLOSED,
          );
        }
      }
    }, this);
  },

  onToggleActiveTheme() {
    this._toggleStateProperty("activeTheme", AppConstants.PANEL_THEME);
  },

  onShowTheme(tab) {
    const currentState = this.state.get("activeTheme");
    if (currentState == null) {
      this.state = this.state.set("activeTheme", AppConstants.PANEL_THEME);
      if (tab) {
        this.state = this.state.setIn(["themePanel", "activeTab"], tab);
      }
      this.trigger(this.state);
    }
  },

  _toggleStateProperty(propName, activeVal) {
    const currentState = this.state.get(propName);
    if (currentState == null) {
      this.state = this.state.set(propName, activeVal);
    } else {
      this.state = this.state.set(propName, null);
    }
    this.trigger(this.state);
  },

  onResetElementPropertiesPanel(type) {
    const currentPanel = this.state.getIn(
      ["propertiesPanel", "elementPropertiesPanel", "activeSection"],
      AppConstants.PANEL_SECTION_ELEMENT_SIZE,
    );
    let activePanel = currentPanel;
    const defaultPanels = [
      AppConstants.PANEL_SECTION_ELEMENT_SIZE,
      AppConstants.PANEL_SECTION_ELEMENT_ACTIONS,
      AppConstants.PANEL_SECTION_ELEMENT_ANIMATION,
      AppConstants.PANEL_SECTION_ELEMENT_HOVER,
      AppConstants.PANEL_SECTION_ELEMENT_EFFECTS,
      AppConstants.PANEL_SECTION_ELEMENT_CLASSNAME,
    ];

    if (_.indexOf(defaultPanels, currentPanel) < 0) {
      const availablePanels = [];
      switch (type) {
        case AppConstants.ELEMENT_TYPE_SIMPLE:
          availablePanels.push(AppConstants.PANEL_SECTION_ELEMENT_SIMPLE);
          break;
        case AppConstants.ELEMENT_TYPE_BUTTON:
          availablePanels.push(AppConstants.PANEL_SECTION_ELEMENT_BUTTON);
          break;
        case AppConstants.ELEMENT_TYPE_GRAPHIC:
          availablePanels.push(AppConstants.PANEL_SECTION_ELEMENT_GRAPHIC);
          break;
        case AppConstants.ELEMENT_TYPE_SVG:
          availablePanels.push(AppConstants.PANEL_SECTION_ELEMENT_SVG);
          break;
        case AppConstants.ELEMENT_TYPE_ICON:
          availablePanels.push(AppConstants.PANEL_SECTION_ELEMENT_ICON);
          break;
        case AppConstants.ELEMENT_TYPE_TEXT:
        case AppConstants.ELEMENT_TYPE_HTML_TEXT:
          availablePanels.push(AppConstants.PANEL_SECTION_ELEMENT_TEXT);
          availablePanels.push(AppConstants.PANEL_SECTION_ELEMENT_TEXT_FORM);
          break;
        case AppConstants.ELEMENT_TYPE_VIDEO:
          availablePanels.push(AppConstants.PANEL_SECTION_ELEMENT_VIDEO);
          break;
        case AppConstants.ELEMENT_TYPE_LOTTIE:
          availablePanels.push(AppConstants.PANEL_SECTION_ELEMENT_LOTTIE);
          break;
        case AppConstants.ELEMENT_TYPE_GALLERY:
          availablePanels.push(AppConstants.PANEL_SECTION_ELEMENT_GALLERY);
          availablePanels.push(
            AppConstants.PANEL_SECTION_ELEMENT_GALLERY_IMAGES,
          );
          break;
        case AppConstants.ELEMENT_TYPE_LINE:
          availablePanels.push(AppConstants.PANEL_SECTION_ELEMENT_LINE);
          break;
        case AppConstants.ELEMENT_TYPE_SOCIAL_GRID:
          availablePanels.push(AppConstants.PANEL_SECTION_ELEMENT_SOCIAL_GRID);
          availablePanels.push(
            AppConstants.PANEL_SECTION_ELEMENT_FALLBACK_IMAGES,
          );
          break;
        case AppConstants.ELEMENT_TYPE_IFRAME:
          availablePanels.push(AppConstants.PANEL_SECTION_ELEMENT_IFRAME);
          break;
        case AppConstants.ELEMENT_TYPE_FORM_INPUT:
        case AppConstants.ELEMENT_TYPE_FORM_SELECT:
        case AppConstants.ELEMENT_TYPE_FORM_RADIO:
        case AppConstants.ELEMENT_TYPE_FORM_CHECKBOX:
          availablePanels.push(AppConstants.PANEL_SECTION_ELEMENT_FORM_FIELD);
          break;
        default:
          availablePanels.push(AppConstants.PANEL_SECTION_ELEMENT_SIZE);
      }
      if (_.indexOf(availablePanels, currentPanel) < 0) {
        [activePanel] = availablePanels;
      }
    }

    if (
      type === AppConstants.ELEMENT_TYPE_BUTTON &&
      currentPanel !== AppConstants.PANEL_SECTION_ELEMENT_ACTIONS
    ) {
      activePanel = AppConstants.PANEL_SECTION_ELEMENT_ACTIONS;
    }

    if (activePanel !== currentPanel) {
      this.state = this.state.setIn(
        ["propertiesPanel", "elementPropertiesPanel", "activeSection"],
        activePanel,
      );
      this.trigger(this.state);
    }
  },

  onToggleActiveFontManagerTab(data) {
    this.state = this.state.setIn(["fontsPanel", "activeTab"], data.tabName);
    this.trigger(this.state);
  },

  onToggleActiveThemeSettingTab(data) {
    this.state = this.state.setIn(["themePanel", "activeTab"], data.tabName);
    this.trigger(this.state);
  },

  onShowLeftPanelView(data) {
    let panel = this.state.get("leftPanel");
    const isOpen = panel.get("open");

    panel = panel.set("open", !isOpen);

    const leftPanel = this.state.get("leftPanel");

    if (!leftPanel.equals(panel)) {
      this.state = this.state.set("leftPanel", panel);
      if (!isOpen) this._closeOtherPanelInPortrait("rightPanel");
      this.trigger(this.state);
    }
  },

  onShowRightPanelView(data) {
    let panel = this.state.get("rightPanel");
    const isOpen = panel.get("open");

    panel = panel.set("open", !isOpen);

    const rightPanel = this.state.get("rightPanel");

    if (!rightPanel.equals(panel)) {
      this.state = this.state.set("rightPanel", panel);
      if (!isOpen) this._closeOtherPanelInPortrait("leftPanel");
      this.trigger(this.state);
    }
  },

  onChangeStageView(viewType) {
    let stage = this.state.get("stage");

    stage = stage.set("view", viewType);

    this.state = this.state.set("stage", stage);

    this.trigger(this.state);
  },

  onSetUIScale(scale) {
    setBaseRem(scale);
    this.state = this.state.set("uiScale", scale);
    this.trigger(this.state);

    this._completeScaleAfterTransition();
  },

  onMoveUIScale(direction) {
    const currentScale = this.state.get("uiScale");
    const newScale = `${+currentScale.split("px")[0] + direction * 2}px`;
    setBaseRem(newScale);
    this.state = this.state.set("uiScale", newScale);
    this.trigger(this.state);

    this._completeScaleAfterTransition();
  },

  _completeScaleAfterTransition() {
    // Trigger the scale complete action after state update to fix those selection outlines/handles
    const root = document.documentElement;
    const animations = root.getAnimations();

    // Look for relevant transitions
    const scaleTransitions = animations.filter(
      (animation) =>
        animation.effectName === "font-size" ||
        animation.transitionProperty === "font-size",
    );

    Promise.all(scaleTransitions.map((animation) => animation.finished)).then(
      () => {
        UIActions.uiScaleComplete();
      },
    );
  },

  onIncrementZoom(increment, layout) {
    const stage = this.state.get("stage");
    const zoom = stage.getIn([layout, "zoom"]);

    const incremented = new Big(zoom).plus(increment).toNumber();
    const clamped = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, incremented));

    if (zoom !== clamped) {
      const updatedStage = stage.setIn([layout, "zoom"], clamped);
      this.state = this.state.set("stage", updatedStage);

      this.trigger(this.state);
    }
  },

  onSetZoom(zoomAmount, layout) {
    let stage = this.state.get("stage");
    stage = stage.setIn([layout, "zoom"], zoomAmount);

    this.state = this.state.set("stage", stage);

    this.trigger(this.state);
  },

  onZoomToWidth(layout, blockContainer) {
    const stageContainer = document.getElementById(`${layout}_stage`);
    const stageWidth =
      stageContainer.offsetWidth -
      VERTICAL_RULER_WIDTH -
      (layout === AppConstants.LAYOUT_DESKTOP ? 0 : MOBILE_OVERLAY_OFFSET);

    const elementOutsidePageDistance =
      findXOutlierDistance(blockContainer, layout) * 2;
    const fullLayoutWidth =
      (layout === AppConstants.LAYOUT_DESKTOP
        ? LAYOUT_DESKTOP_WIDTH
        : LAYOUT_MOBILE_WIDTH + MOBILE_OVERLAY_OFFSET) +
      elementOutsidePageDistance;
    const newZoomAmount =
      ((stageWidth / fullLayoutWidth) * ZOOM_TO_FIT_SCALE) / 100;

    const clampedZoomAmount = Math.max(MIN_ZOOM, newZoomAmount);

    UIActions.setZoom(clampedZoomAmount, layout);
  },

  onZoomReset(layout) {
    let stage = this.state.get("stage");
    stage = stage.setIn([layout, "zoom"], 1);

    this.state = this.state.set("stage", stage);

    this.trigger(this.state);
  },

  onSelectActiveLayout(data) {
    const view = this.state.getIn(["stage", "view"]);

    if (view != AppConstants.STAGE_VIEW_SPLIT) {
      this.state = this.state.setIn(["stage", "view"], data.layout);
    }

    // Send the layout selection to the other stores
    UIActions.selectLayout(data);

    this.trigger(this.state);
  },

  onToggleStageOverlay(type) {
    const overlay = this.state.getIn(["stage", "overlays", type], 0);
    this.state = this.state.setIn(
      ["stage", "overlays", type],
      overlay == 1 ? 0 : 1,
    );

    this._saveUIPreferences();

    this.trigger(this.state);
  },

  onToggleAssetChooser(config) {
    const active = this.state.get("activeAssetChooser", false);

    this.state = this.state.set("activeAssetChooser", active ? null : config);

    this.trigger(this.state);
  },

  onToggleJSONEditor() {
    this.state = this.state.set(
      "jsonEditorActive",
      !this.state.get("jsonEditorActive"),
    );
    this.trigger(this.state);
  },

  onTogglePropertiesPanel(panelInfo) {
    const panelName = panelInfo.name;
    this.state = this.state.setIn(
      ["propertiesPanel", "activePanel"],
      panelName,
    );
    this.trigger(this.state);
  },

  onTogglePagePropertiesSection(sectionInfo) {
    const sectionName = sectionInfo.name;
    this.state = this.state.setIn(
      ["propertiesPanel", "pagePropertiesPanel", "activeSection"],
      sectionName,
    );
    this.trigger(this.state);
  },

  onToggleBlockPropertiesSection(sectionInfo) {
    const sectionName = sectionInfo.name;
    this.state = this.state.setIn(
      ["propertiesPanel", "blockPropertiesPanel", "activeSection"],
      sectionName,
    );
    this.trigger(this.state);
  },

  onToggleStatePropertiesSection(sectionInfo) {
    const sectionName = sectionInfo.name;
    this.state = this.state.setIn(
      ["propertiesPanel", "statePropertiesPanel", "activeSection"],
      sectionName,
    );
    this.trigger(this.state);
  },

  onToggleElementPropertiesSection(sectionInfo) {
    const sectionName = sectionInfo.name;
    this.state = this.state.setIn(
      ["propertiesPanel", "elementPropertiesPanel", "activeSection"],
      sectionName,
    );
    this.trigger(this.state);
  },

  onToggleElementsPropertiesSection(sectionInfo) {
    const sectionName = sectionInfo.name;
    this.state = this.state.setIn(
      ["propertiesPanel", "elementsPropertiesPanel", "activeSection"],
      sectionName,
    );
    this.trigger(this.state);
  },

  onTogglePages({ activePanel = AppConstants.PANEL_PAGES }) {
    this.state = this.state.setIn(["sitePanel", "activePanel"], activePanel);
    this.trigger(this.state);
  },

  onReplaceActiveItemHistory({ type, id, name }) {
    this.state = this.state.setIn(
      ["sitePanel", "activePanelItemHistory"],
      fromJS([[type, id, name]]),
    );

    this.trigger(this.state);
  },

  onAddActiveItemHistory({ type, id = "", name = "" }) {
    const activePanelItemHistory = this.state.getIn([
      "sitePanel",
      "activePanelItemHistory",
    ]);
    this.state = this.state.setIn(
      ["sitePanel", "activePanelItemHistory"],
      activePanelItemHistory.push([type, id, name]),
    );
    this.trigger(this.state);
  },

  onRemoveLastActiveItemHistory() {
    const activePanelItemHistory = this.state.getIn([
      "sitePanel",
      "activePanelItemHistory",
    ]);
    this.state = this.state.setIn(
      ["sitePanel", "activePanelItemHistory"],
      activePanelItemHistory.pop(),
    );
    this.trigger(this.state);
  },

  getActiveItemHistory() {
    return this.state.getIn(["sitePanel", "activePanelItemHistory"]);
  },

  onToggleRenamePopup(config) {
    const active = this.state.get("renamePopup", false);

    this.state = this.state.set("renamePopup", active ? null : config);

    this.trigger(this.state);
  },

  onToggleWorkingOverlay(data) {
    let working = this.state.get("working");

    if (_.has(data, "working")) {
      working = data.working;
    } else {
      working = !working;
    }

    this.state = this.state.set("working", working);

    if (working) {
      let message = "Working...";
      if (_.has(data, "message")) {
        message = data.message;
      }
      this.state = this.state.set("workingMessage", message);
    }

    this.trigger(this.state);
  },

  onShowImageUploadOverlay(data) {
    const message = _.get(data, "message");
    this.state = this.state.setIn(["imageUploadOverlay", "show"], true);
    this.state = this.state.setIn(["imageUploadOverlay", "message"], message);
    this.trigger(this.state);
  },

  onHideImageUploadOverlay() {
    this.state = this.state.setIn(["imageUploadOverlay", "show"], false);
    this.trigger(this.state);
  },

  getState() {
    return this.state;
  },

  getUIScale() {
    return this.state.get("uiScale", UI_SCALE_REGULAR);
  },

  setState(state) {
    this.state = state;

    this.trigger(this.state);
  },

  getColorMode() {
    return this.state.get("colorMode");
  },

  getLeftPanelSettings() {
    return this.state.get("leftPanel");
  },

  getRightPanelSettings() {
    return this.state.get("rightPanel");
  },

  getStageSettings() {
    return this.state.get("stage");
  },

  getStageView() {
    return this.state.getIn(["stage", "view"]);
  },

  getSelectedModalTab(modalName) {
    return this.state.getIn(["modals", modalName, "selectedTab"]);
  },

  getCurrentState() {
    return {
      state: this.state,
    };
  },

  _saveUIPreferences(newPrefs = {}) {
    const prefs = {
      stage: {
        overlays: {
          mobile: this.state.getIn([
            "stage",
            "overlays",
            AppConstants.STAGE_OVERLAY_MOBILE,
          ]),
          guides: this.state.getIn([
            "stage",
            "overlays",
            AppConstants.STAGE_OVERLAY_GUIDES,
          ]),
          columnGuides: this.state.getIn(
            ["stage", "overlays", AppConstants.STAGE_OVERLAY_COLUMN_GUIDES],
            0,
          ),
          centerGuides: this.state.getIn(
            ["stage", "overlays", AppConstants.STAGE_OVERLAY_CENTER_GUIDES],
            0,
          ),
          enableSnapping: this.state.getIn(
            ["stage", "overlays", AppConstants.STAGE_ENABLE_SNAPPING],
            1,
          ),
        },
      },
      ...newPrefs,
    };

    try {
      localStorage.setItem(AppConstants.PREFERENCES_KEY, JSON.stringify(prefs));
    } catch (e) {}
  },

  _loadUIPreferences() {
    try {
      let prefs = localStorage.getItem(AppConstants.PREFERENCES_KEY);

      // if color mode in local storage, override system color mode
      if (prefs) {
        prefs = JSON.parse(prefs);
        this.state = this.state.mergeDeep(Immutable.fromJS(prefs));
        if (prefs.colorMode) {
          this.onChangeColorMode(prefs.colorMode);
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Failed to load UI preferences:", e);
    }
  },

  applyState(data) {
    // don't revert modal states from history
    const modalStates = this.state.get("modals");

    this.state = data.state;
    this.state = this.state.set("modals", modalStates);

    this.trigger(this.state);
  },

  /**
   * Changes the mode
   * @param {typeof CROP_MODE | typeof DEFAULT_MODE} modeName
   */
  onSetMode(modeName) {
    const modeData = MODE_CONFIGS.get(modeName);

    if (!modeData) return;

    const undoRedo = modeData.get("undoRedo");

    if (undoRedo !== undefined) {
      UndoRedoManager.setEnabled(undoRedo);
    }

    this.state = this.state.set("mode", modeData);
    this.trigger();
  },

  getModeType() {
    return this.state.getIn(["mode", "type"]);
  },
});

export default DesignViewStore;
